import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { RESOURCE_REDIRECTS } from '@/components/legal/resourcePages';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { AuthProvider } from '@/lib/auth';
import { QueryProvider } from '@/lib/query';
import { ThemeProvider } from '@/lib/theme';
import { TierProvider } from '@/contexts/TierContext';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { IntercomProvider } from '@/components/layout/IntercomProvider';
import { GoogleAdsAgentTag } from '@/components/layout/GoogleAdsAgentTag';
import { GoogleAnalyticsTag } from '@/components/layout/GoogleAnalyticsTag';
import { LinkedInInsightTag } from '@/components/layout/LinkedInInsightTag';
import { ContentsquareProvider } from '@/components/layout/ContentsquareProvider';
import { AnalyticsProvider } from '@/components/layout/AnalyticsProvider';
import { CookieBanner } from '@/components/layout/CookieBanner';
import { PlaybookPopup } from '@/components/marketing/PlaybookPopup';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';

// aikka OS landing — the new home (AIKKA-OS-INTEGRATION.md §D1). The legacy
// StorePage stays reachable at /store until its sections are re-skinned.
const OsLandingPage = lazyWithReload(() => import('@os/pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const StorePage = lazyWithReload(() => import('@/pages/StorePage').then((m) => ({ default: m.StorePage })));
const LoginPage = lazyWithReload(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazyWithReload(() => import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const AuthCallbackPage = lazyWithReload(() => import('@/pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage })));
const PharmaGeoPage = lazyWithReload(() => import('@/pages/PharmaGeoPage').then((m) => ({ default: m.PharmaGeoPage })));
const KolPage = lazyWithReload(() => import('@/pages/KolPage').then((m) => ({ default: m.KolPage })));
const MedicalWatchPage = lazyWithReload(() => import('@/pages/MedicalWatchPage').then((m) => ({ default: m.MedicalWatchPage })));
const PersonaPage = lazyWithReload(() => import('@/pages/PersonaPage').then((m) => ({ default: m.PersonaPage })));
const MarketAccessPage = lazyWithReload(() => import('@/pages/MarketAccessPage').then((m) => ({ default: m.MarketAccessPage })));
const PharmaGeoLandingPage = lazyWithReload(() => import('@/pages/PharmaGeoLandingPage').then((m) => ({ default: m.PharmaGeoLandingPage })));
const WhyAikkaPage = lazyWithReload(() => import('@/pages/WhyAikkaPage').then((m) => ({ default: m.WhyAikkaPage })));
const CommunityPage = lazyWithReload(() => import('@/pages/CommunityPage').then((m) => ({ default: m.CommunityPage })));
const LegalPage = lazyWithReload(() => import('@/pages/LegalPage').then((m) => ({ default: m.LegalPage })));
const PrivacyPage = lazyWithReload(() => import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazyWithReload(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const SecurityPage = lazyWithReload(() => import('@/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })));
const SupportPage = lazyWithReload(() => import('@/pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const DocsPage = lazyWithReload(() => import('@/pages/DocsPage').then((m) => ({ default: m.DocsPage })));
const ChangelogPage = lazyWithReload(() => import('@/pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage })));
// Les pages annexes ajoutées à la passe pré-lancement : /cookies (le document
// que cite le bandeau de consentement), /resources (leur index, destination de
// la puce qui remplace « Security » dans le pied de page de la home) et les
// deux sous-pages de confidentialité qu'ouvre Paramètres → Confidentialité.
const CookiesPage = lazyWithReload(() => import('@/pages/CookiesPage').then((m) => ({ default: m.CookiesPage })));
const ResourcesPage = lazyWithReload(() => import('@/pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage })));
const PlaybookPage = lazyWithReload(() => import('@/pages/PlaybookPage').then((m) => ({ default: m.PlaybookPage })));
const DataProtectionPage = lazyWithReload(() => import('@/pages/DataPage').then((m) => ({ default: m.DataProtectionPage })));
const DataUsePage = lazyWithReload(() => import('@/pages/DataPage').then((m) => ({ default: m.DataUsePage })));
const PublicKolPage = lazyWithReload(() => import('@/pages/public/KolPage').then((m) => ({ default: m.KolPage })));
const PublicMedicalWatchPage = lazyWithReload(() =>
  import('@/pages/public/MedicalWatchPage').then((m) => ({ default: m.MedicalWatchPage })),
);
const PublicPersonaPage = lazyWithReload(() => import('@/pages/public/PersonaPage').then((m) => ({ default: m.PersonaPage })));
const PublicMarketAccessPage = lazyWithReload(() =>
  import('@/pages/public/MarketAccessPage').then((m) => ({ default: m.MarketAccessPage })),
);
const BillingPage = lazyWithReload(() => import('@/pages/BillingPage').then((m) => ({ default: m.BillingPage })));
const CheckoutSuccessPage = lazyWithReload(() => import('@/pages/CheckoutSuccessPage').then((m) => ({ default: m.CheckoutSuccessPage })));
const CheckoutCancelPage = lazyWithReload(() => import('@/pages/CheckoutCancelPage').then((m) => ({ default: m.CheckoutCancelPage })));
const TierDemoPage = lazyWithReload(() => import('@/pages/TierDemoPage').then((m) => ({ default: m.TierDemoPage })));
const NotFoundPage = lazyWithReload(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
// aikka OS persistent shell — sidebar always mounted, the panel is an <Outlet/>.
// /agent, /spaces, /agent/scheduled and /artefacts are its children so the
// sidebar never unmounts (claude.ai information-architecture parity).
const OsShell = lazyWithReload(() =>
  import('@os/OsShell').then((m) => ({ default: m.OsShell })),
);
// aikka OS agent workspace — what "Enter the agent" leads to (§D2).
const AgentWorkspace = lazyWithReload(() =>
  import('@os/pages/AgentWorkspace').then((m) => ({ default: m.AgentWorkspace })),
);
// aikka OS Artéfacts — the deliverables gallery, wired to GET /agent/artifacts.
const ArtifactsPage = lazyWithReload(() =>
  import('@os/pages/ArtifactsPage').then((m) => ({ default: m.ArtifactsPage })),
);
// Scheduled recurring runs surface (Programmés) — wired to /v1/intel/watchers.
const ScheduledRunsPage = lazyWithReload(() =>
  import('@os/pages/ScheduledRunsPage').then((m) => ({ default: m.ScheduledRunsPage })),
);
// A single scheduled task's dedicated page (instructions, cadence, history…).
const ScheduledRunDetailPage = lazyWithReload(() =>
  import('@os/pages/ScheduledRunDetailPage').then((m) => ({ default: m.ScheduledRunDetailPage })),
);
// aikka OS Espaces — THE container surface (parity with claude.ai Projects),
// full-bleed like /agent and outside DashboardLayout. Since the merge (L1-5) a
// Space is the single object: the folder AND the brand context, on the user.
const SpacesPage = lazyWithReload(() =>
  import('@os/pages/SpacesPage').then((m) => ({ default: m.SpacesPage })),
);
const SpaceDetailPage = lazyWithReload(() =>
  import('@os/pages/SpaceDetailPage').then((m) => ({ default: m.SpaceDetailPage })),
);
// aikka OS Chats — the "View all" surface (reached from the Récents header),
// listing every conversation newest-first with a title search. In-pane like
// /spaces and /artefacts, so the sidebar persists.
const ChatsPage = lazyWithReload(() =>
  import('@os/pages/ChatsPage').then((m) => ({ default: m.ChatsPage })),
);
const PublicShell = lazyWithReload(() =>
  import('@os/components/PublicShell').then((m) => ({ default: m.PublicShell })),
);
const SharedRunPage = lazyWithReload(() =>
  import('@os/pages/SharedRunPage').then((m) => ({ default: m.SharedRunPage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
// La destination du lien de l'e-mail de confirmation. Elle n'existait pas : le
// courrier pointait vers `/#/verify-email`, une route de hash sur une
// application montée en BrowserRouter, vers un chemin non déclaré.
const VerifyEmailPage = lazyWithReload(() =>
  import('@/pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
// L'écran qui suit l'inscription. Le courrier partait sans que rien ne le dise,
// et depuis que la confirmation barre les runs ce silence était un mur.
const CheckEmailPage = lazyWithReload(() =>
  import('@/pages/CheckEmailPage').then((m) => ({ default: m.CheckEmailPage })),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="space-y-4 w-full max-w-md">
        <Skeleton className="h-8 w-48 animate-shimmer" />
        <Skeleton className="h-4 w-full animate-shimmer" />
        <Skeleton className="h-4 w-3/4 animate-shimmer" />
        <Skeleton className="h-32 w-full animate-shimmer" />
      </div>
    </div>
  );
}


/**
 * A bookmarked `/projects/:id` lands on the SAME object under its new name: the
 * merge (L1-5) kept each project's id as its Space id, so the redirect can carry
 * the id through instead of dumping the operator on a generic page.
 */
function ProjectRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/spaces/${id}` : '/spaces'} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      {/* LA MESURE D'AUDIENCE — juste SOUS le routeur, parce qu'elle lit
          `useLocation`, et AU-DESSUS de tout le reste, parce qu'une page vue
          doit partir quelle que soit la route. Le conteneur GTM est chargé par
          `index.html` ; ce provider est ce qui lui dit qu'on a navigué, et ce
          que le visiteur a consenti. Sans lui, GA4 ne compte que le premier
          chargement du document — c'est-à-dire `/`, et rien d'autre. */}
      <AnalyticsProvider>
      <QueryProvider>
        <AuthProvider>
          {/* Le thème est monté ici, au-dessus de TOUTES les routes : la portée
              tranchée (§6) est le produit authentifié — OS, dashboard,
              facturation, écrans d'auth — et un thème qui appartient à un shell
              s'éteint dès qu'on en sort. Sous AuthProvider (la préférence vit
              sur le compte) et sous QueryProvider (c'est le même cache que les
              autres préférences). */}
          <ThemeProvider>
          {/* La mesure d'audience (tag Contentsquare, « Hotjar ») couvre TOUTES
              les routes : elle écoute le consentement, et le bandeau peut être
              accepté depuis n'importe laquelle. */}
          <ContentsquareProvider>
          {/* Intercom est monté aussi haut, mais pour une raison différente : le
              messager ne s'affiche QUE sur l'agent (`MESSENGER_ROUTES`), et
              c'est justement en quittant l'agent qu'un widget déjà démarré doit
              être éteint. Un provider monté sous les routes concernées ne
              pourrait pas l'éteindre — il serait démonté avec elles. */}
          <IntercomProvider>
          {/* LE TAG GOOGLE ANALYTICS 4 DU SITE — G-X0T2CKRTQB, sur TOUTES
              les routes. Le conteneur GTM du head est installé mais son espace
              publié est vide depuis août : c'est le site qui porte la mesure
              maintenant (voir `components/layout/GoogleAnalyticsTag.tsx`).
              Le jour où un tag GA4 est publié dans GTM, retirer ce composant
              ou le sien — sinon chaque page vue comptera deux fois. */}
          <GoogleAnalyticsTag />
          {/* LE TAG GOOGLE ADS DE L'AGENT — voir `components/layout/
              GoogleAdsAgentTag.tsx`. Monté au niveau des providers parce
              qu'il lit `useLocation` : c'est en ARRIVANT sur `/agent` — premier
              chargement comme navigation cliente — qu'il pose le script
              `AW-17976349261`. Ailleurs, il ne rend et ne charge rien. */}
          <GoogleAdsAgentTag />
          {/* LE TAG LINKEDIN INSIGHTS DE L'AGENT — même portée que le tag
              Google Ads juste au-dessus (`agentRoute.ts`), mais derrière
              l'accord analytics : LinkedIn n'a pas de Consent Mode, c'est le
              bandeau qui porte la promesse de /privacy. */}
          <LinkedInInsightTag />
          <TierProvider>
          <TooltipProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<OsLandingPage />} />
                {/* Marketing pages — grouped under the brand-system shell so
                    the legacy stylesheet bridge applies to all of them at once.
                    `theme="light"` : le site public reste clair (§6), et depuis
                    que `os-theme-dark` est posé sur <html> avant l'hydratation,
                    ne rien stamper ne veut plus dire « clair ». */}
                <Route element={<PublicShell theme="light" />}>
                  <Route path="/store" element={<StorePage />} />
                  {/* `/pricing` est SUPPRIMÉE (demande client). Pas de
                      redirection : la suppression est franche, la route n'existe
                      plus. Le tarif reste énoncé par la bande « Pay as you go »
                      de la home (ancre `#pricing`), et l'achat se fait
                      désormais dans le produit, par la pop-up de recharge
                      (`TopUpDialog`) — c'est le tunnel que le client a choisi.
                      Les entrées « Tarifs » du pied de page public, de la barre
                      de nav et du menu utilisateur partent avec elle : un lien
                      vers une route morte est pire que pas de lien. */}
                </Route>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/check-email" element={<CheckEmailPage />} />

                {/* Public share link — deliberately OUTSIDE <ProtectedRoute>:
                    the whole point is that a recipient without an account can
                    read it. Content is a frozen, redacted snapshot. */}
                <Route path="/s/:shareId" element={<SharedRunPage />} />

                {/* Billing callback routes — Stripe returns here, so they are
                    reached signed-in but outside the dashboard shell. Ce groupe
                    ne stampe RIEN et suit la racine : §6 le tranche IN (retour
                    de paiement d'un opérateur dont le produit est noir). */}
                <Route element={<PublicShell />}>
                  <Route path="/billing/success" element={<CheckoutSuccessPage />} />
                  <Route path="/billing/cancel" element={<CheckoutCancelPage />} />
                  <Route path="/tier-demo" element={<TierDemoPage />} />
                </Route>

                {/* Public product & info pages — claires par décision (§6). */}
                <Route element={<PublicShell theme="light" />}>
                  <Route path="/pharmageo" element={<PharmaGeoLandingPage />} />
                  <Route path="/why-aikka" element={<WhyAikkaPage />} />
                  <Route path="/community" element={<CommunityPage />} />
                  {/* LES PAGES ANNEXES, TOUTES SOUS `/resources`.
                      « tu fusionnes sous resource » : les cinq documents et les
                      deux sous-pages de confidentialité ont quitté le premier
                      niveau. Les chemins viennent de `resourcePages.ts`, qui est
                      aussi ce que lisent la barre latérale, l'index et les deux
                      pieds de page — les recopier ici les ferait diverger. */}
                  <Route path="/resources" element={<ResourcesPage />} />
                  {/* Lead magnet septembre 2026 (US Top 50). Les deux adresses
                      courtes circulent dans le PDF d'aperçu et le post LinkedIn. */}
                  <Route path="/resources/pharma-geo-playbook-2026" element={<PlaybookPage />} />
                  <Route path="/playbook-2026" element={<Navigate to="/resources/pharma-geo-playbook-2026" replace />} />
                  <Route path="/playbook" element={<Navigate to="/resources/pharma-geo-playbook-2026" replace />} />
                  <Route path="/resources/legal" element={<LegalPage />} />
                  <Route path="/resources/privacy" element={<PrivacyPage />} />
                  <Route
                    path="/resources/privacy/data-protection"
                    element={<DataProtectionPage />}
                  />
                  <Route path="/resources/privacy/data-use" element={<DataUsePage />} />
                  <Route path="/resources/terms" element={<TermsPage />} />
                  <Route path="/resources/cookies" element={<CookiesPage />} />
                  <Route path="/resources/security" element={<SecurityPage />} />
                  <Route path="/resources/support" element={<SupportPage />} />
                  <Route path="/resources/docs" element={<DocsPage />} />
                  <Route path="/resources/changelog" element={<ChangelogPage />} />

                  {/* ET LES SEPT ANCIENNES ADRESSES CONTINUENT DE RÉPONDRE.
                      Ce sont des pages légales : publiées, indexées, et citées
                      nommément par le bandeau de consentement. Une URL légale
                      qui renvoie 404 est un défaut, pas un déménagement — d'où
                      la redirection plutôt que la suppression. `replace` pour
                      que le bouton « précédent » ne rejoue pas la redirection.
                      La liste est DÉRIVÉE des pages : déplacer un document
                      emporte sa redirection avec lui. */}
                  {RESOURCE_REDIRECTS.map(({ from, to }) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}
                  <Route path="/products/kol" element={<PublicKolPage />} />
                  <Route path="/products/medical-watch" element={<PublicMedicalWatchPage />} />
                  <Route path="/products/persona" element={<PublicPersonaPage />} />
                  <Route path="/products/market-access" element={<PublicMarketAccessPage />} />
                </Route>

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  {/* aikka OS persistent shell — THE authenticated experience.
                      The sidebar is mounted once here and never torn down; the
                      panels below are children swapped through the <Outlet/>, so
                      moving between the chat, Projets, Programmé and Artéfacts
                      never unmounts the sidebar (claude.ai IA parity). It sits
                      OUTSIDE DashboardLayout (full-bleed h-dvh + its own rail),
                      which is why /app, /agent/chat and /history were
                      removed rather than kept alongside (§12). */}
                  <Route element={<OsShell />}>
                    <Route path="/agent" element={<AgentWorkspace />} />
                    {/* Scheduled recurring runs (Programmés) — now an in-pane view. */}
                    <Route path="/agent/scheduled" element={<ScheduledRunsPage />} />
                    {/* One scheduled task's dedicated page — instructions, cadence,
                        settings and its full execution history, in-pane. */}
                    <Route path="/agent/scheduled/:id" element={<ScheduledRunDetailPage />} />
                    {/* Espaces list — organise conversations + brand context, in-pane. */}
                    <Route path="/spaces" element={<SpacesPage />} />
                    {/* A single Space's detail — in-pane too, so the sidebar
                        persists on /spaces/:id. Until L1-5 this route existed
                        only as a redirect back to /agent, so every Space row in
                        the rail was a dead link. */}
                    <Route path="/spaces/:id" element={<SpaceDetailPage />} />
                    {/* Artéfacts — deliverables gallery panel (placeholder). */}
                    <Route path="/artefacts" element={<ArtifactsPage />} />
                    {/* Chats — the "View all" list of every conversation, in-pane. */}
                    <Route path="/chats" element={<ChatsPage />} />
                  </Route>

                  {/* Old entry points people may still have bookmarked. */}
                  {/* Settings is no longer a page — it is the unified SettingsModal,
                      opened from the OS shell avatar menu. The route is retired to
                      a redirect so existing bookmarks land on the app rather than
                      404 (parité claude.ai: settings is a pop-up, not a page). */}
                  <Route path="/dashboard/settings" element={<Navigate to="/agent" replace />} />
                  <Route path="/app/*" element={<Navigate to="/agent" replace />} />
                  <Route path="/agents" element={<Navigate to="/agent" replace />} />
                  <Route path="/agents/:agentId" element={<Navigate to="/agent" replace />} />
                  {/* Projets became Espaces (L1-5). A project id IS its Space id
                      (the migration kept it), so a bookmarked /projects/:id lands
                      on the very same object rather than on a 404 or a generic
                      redirect that loses which one was meant. */}
                  <Route path="/projects" element={<Navigate to="/spaces" replace />} />
                  <Route path="/projects/:id" element={<ProjectRedirect />} />
                  <Route path="/history" element={<Navigate to="/agent" replace />} />

                  {/* Legacy dashboard shell (billing, settings, product pages) */}
                  <Route element={<DashboardLayout />}>
                    {/* Dashboard home redirects to the unified agent */}
                    <Route path="/dashboard" element={<Navigate to="/agent" replace />} />

                    {/* Product routes */}
                    <Route path="/dashboard/pharmageo" element={<PharmaGeoPage />} />
                    <Route path="/dashboard/kol" element={<KolPage />} />
                    <Route path="/dashboard/medical-watch" element={<MedicalWatchPage />} />
                    <Route path="/dashboard/personas" element={<PersonaPage />} />
                    <Route path="/dashboard/market-access" element={<MarketAccessPage />} />

                    {/* Account — billing stays a page for the Stripe checkout
                        return + legacy shell; settings is now the modal only. */}
                    <Route path="/dashboard/billing" element={<BillingPage />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            {/* LE BANDEAU COOKIES — monté une seule fois, hors des routes.
                Il doit se poser sur la home comme sur /pricing comme sur
                /agent : le monter par page est la façon dont on se retrouve
                avec une route qui ne demande rien. Il se rend `null` de
                lui-même dès qu'un choix est mémorisé, donc il ne coûte rien
                aux visiteurs qui ont déjà répondu.
                Sous `IntercomProvider` : c'est ce provider qui écoute le
                consentement et démarre — ou éteint — le messager. */}
            <CookieBanner />
            {/* L'invitation à 30 s (rapport H2 2026), pages publiques seulement. */}
            <PlaybookPopup />
          </TooltipProvider>
          </TierProvider>
          </IntercomProvider>
          </ContentsquareProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryProvider>
      </AnalyticsProvider>
    </BrowserRouter>
  );
}
